import { IApiAxiosError, removeOrganizationsFromClauseChecklist } from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Stack,
    Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core/useDialogs";

interface IProps {
    /**
     * The organization id to remove the clause checklist from.
     */
    organizationId: number;
    /**
     * The clause checklist ids to remove from the organization.
     */
    clauseChecklistIds: number[];
    /**
     * Selected checklists left out because they are shared with a group.
     */
    skippedCount?: number;
}

/**
 * A dialog for removing a single clause from a clause checklist.
 */
export const RemoveClauseChecklistFromOrganizationDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<IProps>) => {
    const { organizationId, clauseChecklistIds, skippedCount = 0 } = payload;

    const queryClient = useQueryClient();
    const { mutate, isPending, isSuccess, isError, error } = useMutation({
        mutationFn: () =>
            Promise.all(
                clauseChecklistIds.map((checklistId) =>
                    removeOrganizationsFromClauseChecklist(checklistId, [
                        organizationId,
                    ])
                )
            ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-organization-details-clause-checklists"],
            });
        },
    });

    const numChecklists = clauseChecklistIds.length;
    const title = `Remove Clause Checklist${numChecklists > 1 ? "s" : ""}`;
    const description = `Are you sure you want to remove ${
        numChecklists > 1 ? "these" : "this"
    } ${numChecklists} clause checklist${
        numChecklists > 1 ? "s" : ""
    } from the organization?${
        skippedCount > 0
            ? ` ${skippedCount} selected checklist${
                  skippedCount > 1 ? "s are" : " is"
              } shared with a group and won't be removed.`
            : ""
    }`;
    const destructiveAction = title;
    const cancelAction = `No, Keep Clause Checklist${
        numChecklists > 1 ? "s" : ""
    }`;

    const axiosError = error && (error as IApiAxiosError);
    const errorMsg =
        axiosError?.response?.data.detail ||
        `The clause checklist${
            numChecklists > 1 ? "s" : ""
        } could not be removed. Please try again later.`;
    const successMsg = `The clause checklist${
        numChecklists > 1 ? "s have" : " has"
    } been removed from the organization.`;

    return (
        <Dialog open={open} onClose={() => onClose()}>
            <DialogContent sx={{ p: 5 }}>
                <Stack alignItems={"center"} spacing={6}>
                    {isPending && <CircularProgress sx={{ fontSize: 53 }} />}
                    {!isPending && !isSuccess && (
                        <ErrorOutlineIcon color="error" sx={{ fontSize: 53 }} />
                    )}
                    {isSuccess && (
                        <CheckCircleOutlineIcon
                            color="success"
                            sx={{ fontSize: 53 }}
                        />
                    )}
                    <Stack alignItems={"center"}>
                        <Typography
                            variant="h6"
                            fontWeight={600}
                            textTransform={"capitalize"}
                        >
                            {isError && "Error"}
                            {isSuccess && "Success"}
                            {!isSuccess && !isError && title}
                        </Typography>
                        <Typography textAlign={"center"}>
                            {isError && errorMsg}
                            {isSuccess && successMsg}
                            {!isSuccess && !isError && description}
                        </Typography>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{ px: 5, pb: 5, justifyContent: "center", gap: 3 }}
            >
                {!isSuccess && !isError && (
                    <>
                        <Button
                            onClick={() => onClose()}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                px: 2.5,
                                color: "text.primary",
                            }}
                            disabled={isPending}
                        >
                            {cancelAction}
                        </Button>
                        <Button
                            onClick={() => mutate()}
                            variant="contained"
                            color="error"
                            sx={{
                                borderRadius: 2,
                                px: 2.5,
                                textTransform: "capitalize",
                            }}
                            disabled={isPending}
                        >
                            {destructiveAction}
                        </Button>
                    </>
                )}
                {(isSuccess || isError) && (
                    <Button
                        onClick={() => onClose()}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            px: 2.5,
                            color: "text.primary",
                        }}
                        disabled={isPending}
                        fullWidth
                    >
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
